import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { getCurrencySymbol, isEuropeanUser } from '../utils/currency';
import { getCreditPackClickEventType } from '../utils/credit-pack-events';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

type CreditPackKey = 'credits_1000' | 'credits_5000' | 'credits_15000';
type BillingCurrency = 'eur' | 'usd';

interface CreditPack {
  key: CreditPackKey;
  credits: number;
  price: number;
  currency: BillingCurrency;
}

interface CreditPacksResponse {
  packs: CreditPack[];
  currency: BillingCurrency;
  eligible: boolean;
  reason: 'free_tier' | null;
}

interface BuyCreditsSectionProps {
  sectionId?: string;
}

export const BuyCreditsSection: React.FC<BuyCreditsSectionProps> = ({ sectionId = 'buy-credits' }) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [eligible, setEligible] = useState(false);
  const [ineligibilityReason, setIneligibilityReason] = useState<CreditPacksResponse['reason']>(null);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [loadingPackKey, setLoadingPackKey] = useState<CreditPackKey | null>(null);
  const currency: BillingCurrency = isEuropeanUser() ? 'eur' : 'usd';
  const currencySymbol = getCurrencySymbol();

  const formatPrice = useMemo(() => {
    return (price: number): string => {
      const formatted = Number.isInteger(price) ? price.toString() : price.toFixed(2);
      return `${currencySymbol}${formatted}`;
    };
  }, [currencySymbol]);

  const fetchCreditPacks = useCallback(async (): Promise<void> => {
    if (!token) {
      setLoadingPacks(false);
      return;
    }

    try {
      setLoadingPacks(true);
      const response = await fetch(`${API_BASE}/api/credit-packs?currency=${currency}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data: CreditPacksResponse = await response.json();
        setPacks(data.packs || []);
        setEligible(data.eligible);
        setIneligibilityReason(data.reason);
      }
    } catch (error) {
      console.error('Error fetching credit packs:', error);
    } finally {
      setLoadingPacks(false);
    }
  }, [token, currency]);

  useEffect(() => {
    fetchCreditPacks();
  }, [fetchCreditPacks]);

  const handleBuyPack = async (pack: CreditPack): Promise<void> => {
    if (!token || !eligible) {
      return;
    }

    setLoadingPackKey(pack.key);
    try {
      try {
        await fetch(`${API_BASE}/api/observability-events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            eventType: getCreditPackClickEventType(pack.key),
            userMessage: `User clicked buy credits (${pack.credits.toLocaleString()} credits, ${formatPrice(pack.price)})`,
          }),
        });
      } catch (error) {
        console.error('Error tracking credit pack buy click:', error);
      }

      const successUrl = `${window.location.origin}/desk/usage-and-billing?creditPurchase=success#${sectionId}`;
      const cancelUrl = `${window.location.origin}/desk/usage-and-billing?creditPurchase=canceled#${sectionId}`;

      const response = await fetch(`${API_BASE}/api/stripe/create-credit-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          packKey: pack.key,
          currency,
          successUrl,
          cancelUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating credit checkout session:', error);
      alert(t('error_creating_checkout'));
    } finally {
      setLoadingPackKey(null);
    }
  };

  const getIneligibilityMessage = (): string => {
    if (ineligibilityReason === 'free_tier') {
      return t('credit_purchase_requires_paid_plan');
    }
    return t('credit_purchase_not_available');
  };

  if (loadingPacks) {
    return (
      <div id={sectionId} style={{ marginTop: '2rem', padding: '0 0.25rem' }} className="sm:px-2">
        <p style={{ color: 'var(--muted-foreground)' }}>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div id={sectionId} style={{ marginTop: '2rem', padding: '0 0.25rem' }} className="sm:px-2">
      <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--foreground)' }}>
        {t('buy_extra_credits')}
      </h2>
      <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginBottom: '1rem' }}>
        {t('buy_extra_credits_description')}
      </p>

      {!eligible && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: 'var(--muted)',
            border: '1px solid var(--border)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--muted-foreground)',
          }}
        >
          {getIneligibilityMessage()}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
          gap: '0.75rem',
        }}
      >
        {packs.map((pack) => (
          <Card key={pack.key} style={{ border: '1px solid var(--border)' }}>
            <CardContent style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--foreground)' }}>
                    {pack.credits.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
                    {t('credits')}
                  </span>
                </div>
                <span style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--foreground)' }}>
                  {formatPrice(pack.price)}
                </span>
              </div>
              <Button
                onClick={() => handleBuyPack(pack)}
                disabled={!eligible || loadingPackKey !== null}
                style={{ width: '100%' }}
              >
                {loadingPackKey === pack.key ? t('loading') : t('buy_credits_pack')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
